import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { groupsApi } from '../services/api';

interface GroupProduct {
  id: string;
  title: string;
  description?: string | null;
  price?: number | null;
  imageUrls: string[];
  externalUrl?: string | null;
  status: string;
  userId: string;
  createdAt: string;
}

const GroupProductDetail: React.FC = () => {
  const { groupId, productId } = useParams<{ groupId: string; productId: string }>();
  const navigate = useNavigate();
  useAuth();
  const [product, setProduct] = useState<GroupProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!groupId || !productId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await groupsApi.getGroupProduct(groupId, productId);
        if (cancelled) return;
        if (res?.success && res?.data) {
          setProduct(res.data);
        } else {
          setError(res?.error || 'Product not found');
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load product');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [groupId, productId]);

  const highlights = (product?.description || '')
    .split(/\n/)
    .map((s) => s.trim())
    .filter(Boolean);

  if (loading) {
    return (
      <Layout>
        <div style={{ padding: '24px', textAlign: 'center', color: '#666' }}>Loading…</div>
      </Layout>
    );
  }

  if (error || !product) {
    return (
      <Layout>
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <p style={{ color: '#c00', marginBottom: '16px' }}>{error || 'Product not found'}</p>
          <button onClick={() => navigate(-1)} style={{ padding: '10px 20px', background: '#667eea', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
            Back
          </button>
        </div>
      </Layout>
    );
  }

  const images = product.imageUrls || [];

  return (
    <Layout>
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '24px' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ marginBottom: '20px', padding: '8px 16px', background: 'rgba(0,0,0,0.05)', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
        >
          ← Back
        </button>

        <div style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          {/* Image gallery */}
          {images.length === 0 ? (
            <div style={{ aspectRatio: '16/9', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '14px' }}>
              No photos
            </div>
          ) : images.length === 1 ? (
            <img src={images[0]} alt={product.title} style={{ width: '100%', display: 'block', objectFit: 'cover' }} />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: images.length >= 2 ? '1fr 1fr' : '1fr', gap: '4px' }}>
              {images.map((url, i) => (
                <img key={i} src={url} alt="" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }} />
              ))}
            </div>
          )}

          <div style={{ padding: '24px' }}>
            <span style={{ display: 'inline-block', padding: '4px 12px', background: '#333', color: 'white', borderRadius: '20px', fontSize: '11px', fontWeight: '700', marginBottom: '12px' }}>
              ON SALE
            </span>
            <h1 style={{ margin: '0 0 12px 0', fontSize: '24px', fontWeight: '700', color: '#1a1a1a' }}>
              {product.title}
            </h1>
            {product.price != null && (
              <p style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: '600', color: '#667eea' }}>
                ${product.price.toLocaleString()}
              </p>
            )}

            {highlights.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                {highlights.map((h, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px', fontSize: '15px', color: '#333' }}>
                    <span>✅</span>
                    <span>{h}</span>
                  </div>
                ))}
              </div>
            )}

            {product.externalUrl && (
              <a
                href={product.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-block', padding: '12px 24px', background: '#667eea', color: 'white', borderRadius: '8px', textDecoration: 'none', fontWeight: '600', marginTop: '8px' }}
              >
                View external listing →
              </a>
            )}

            <p style={{ marginTop: '20px', fontSize: '12px', color: '#888' }}>
              Listed in group • {new Date(product.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default GroupProductDetail;
